import {
  ArrowPathIcon,
  DocumentDuplicateIcon,
  CommandLineIcon,
  FolderIcon,
  PencilIcon,
  TrashIcon,
  WrenchIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { Button, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Tooltip } from '@nextui-org/react';
import { ColumnSize } from '@react-types/table';
import React, { ReactNode } from 'react';
import { useRef, useEffect } from 'react';

import { ServerSchema, ServerUUID, MonitorStatus, ClusterTag } from '@common/types';
import { ConfirmationUi } from '@portal/components/common/Confirmation.ui';
import { IconTooltipPopoverUi } from '@portal/components/common/IconTooltipPopover.ui';
import { connectionTypeMap } from '@portal/constant';
import { cn, copyToClipboard } from '@portal/utils';

type TerminalConnectionStatus = 'none' | 'connected' | 'error' | 'connecting' | 'disconnected';

// Detect Safari browser
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

type Columns = {
  key: string;
  label: string;
  width?: ColumnSize;
};
const getColumns = (showSslPasswords: boolean): Array<Columns> => {
  const baseColumns: Array<Columns> = [
    {
      key: 'server-name',
      label: 'Server Name',
    },
    {
      key: 'server-host',
      label: 'Server Host',
      width: 180,
    },
    {
      key: 'cluster-tag',
      label: 'Cluster Tag',
      width: 120,
    },
    {
      key: 'ssh-port',
      label: 'SSH Port',
      width: 80,
    },
    {
      key: 'php-port',
      label: 'PHP Port',
      width: 80,
    },
    {
      key: 'connection-type',
      label: 'Connection Type',
      width: 100,
    },
    {
      key: 'ssh-username',
      label: 'SSH Username',
      width: 140,
    },
  ];

  if (showSslPasswords) {
    baseColumns.push({
      key: 'ssl-password',
      label: 'SSL Password',
      width: 150,
    });
  }

  baseColumns.push({
    key: 'actions',
    label: 'Actions',
    width: 96,
  });

  return baseColumns;
};

export const ServersTableUI = ({
  paginatedData,
  tableStatus,
  onRemoveServer,
  onUpdateServer,
  onReinitializeServer,
  onRestartServer,
  onOpenServerTerminal,
  onOpenSftp,
  onUpdateSslPassword,
  serverStats,
  terminalStatuses,
  showSslPasswords,
  sslPasswords,
  sslPasswordLoading,
  sslPasswordErrors,
  onFetchSslPassword,
  selectedCluster,
}: {
  paginatedData: ServerSchema[];
  tableStatus: ReactNode;
  onRemoveServer: (serverUUID: ServerUUID, onClose: () => void) => void;
  onUpdateServer: (server: ServerSchema) => void;
  onReinitializeServer: (serverUUID: ServerUUID) => void;
  onRestartServer: (serverUUID: ServerUUID) => void;
  onOpenServerTerminal: (server: ServerSchema) => void;
  onOpenSftp: (server: ServerSchema) => void;
  onUpdateSslPassword: (server: ServerSchema) => void;
  serverStats?: { [key: ServerUUID]: MonitorStatus };
  terminalStatuses?: { [key: ServerUUID]: TerminalConnectionStatus };
  showSslPasswords?: boolean;
  sslPasswords?: Record<ServerUUID, string | null>;
  sslPasswordLoading?: Record<ServerUUID, boolean>;
  sslPasswordErrors?: Record<ServerUUID, string>;
  onFetchSslPassword?: (serverUUID: ServerUUID, serverClusterTag: ClusterTag) => Promise<void>;
  selectedCluster?: string;
}) => {
  const [isNameCopy, setIsNameCopy] = React.useState('');
  const [nameHover, setNameHover] = React.useState('');

  const [isHostCopy, setIsHostCopy] = React.useState('');
  const [hostHover, setHostHover] = React.useState('');

  const [hoveredRow, setHoveredRow] = React.useState<string | null>(null);

  // Track which servers we've initiated fetching for
  const fetchInitiatedRef = useRef<Set<ServerUUID>>(new Set());
  const prevClusterRef = useRef<string | undefined>(selectedCluster);

  // Reset fetch tracking when showSslPasswords or selectedCluster changes
  useEffect(() => {
    if (!showSslPasswords) {
      fetchInitiatedRef.current.clear();
    } else if (prevClusterRef.current !== selectedCluster) {
      // Clear ref when cluster changes to allow re-fetching for new cluster
      fetchInitiatedRef.current.clear();
      prevClusterRef.current = selectedCluster;
    }
  }, [showSslPasswords, selectedCluster]);

  const onCopyName = async (serverName: string) => {
    if (!(await copyToClipboard(serverName))) return;
    setIsNameCopy(serverName);
    setTimeout(() => setIsNameCopy(''), 1000);
  };

  const onCopyHost = async (serverHost: string) => {
    if (!(await copyToClipboard(serverHost))) return;
    setIsHostCopy(serverHost);
    setTimeout(() => setIsHostCopy(''), 1000);
  };

  const columns = getColumns(showSslPasswords || false);

  return (
    <Table removeWrapper isStriped isHeaderSticky aria-label='registered servers list'>
      <TableHeader columns={columns}>
        {column => (
          <TableColumn key={column.key} width={column.width || null}>
            {column.label}
          </TableColumn>
        )}
      </TableHeader>
      <TableBody emptyContent={tableStatus}>
        {paginatedData.map(server => {
          const isHovered = hoveredRow === server.name;

          // Check if server is in failed/error state
          const serverStatus = serverStats?.[server.uuid];
          const isServerFailed = serverStatus?.state === 'errored' || serverStatus?.state === 'initializing-error';

          // Fetch SSL password on-demand when column is visible
          if (showSslPasswords && onFetchSslPassword && !fetchInitiatedRef.current.has(server.uuid)) {
            fetchInitiatedRef.current.add(server.uuid);
            onFetchSslPassword(server.uuid, server.clusterTag).catch(err => {
              console.error(`Error fetching SSL password for ${server.uuid}:`, err);
            });
          }

          // Prepare styles based on browser and hover state
          const rowClassName = isSafari
            ? ''
            : 'group hover:shadow-[0_0_0_2px_rgba(59,130,246,0.8),0_20px_25px_-5px_rgba(59,130,246,0.15),0_10px_10px_-5px_rgba(59,130,246,0.1)] hover:rounded-lg';

          // For Safari: use box-shadow to create border effect (borders can't be rounded on table cells)
          const baseCellShadow = isHovered
            ? 'inset 0 2px 0 0 rgba(59, 130, 246, 0.8), inset 0 -2px 0 0 rgba(59, 130, 246, 0.8), 0 20px 25px -5px rgba(59, 130, 246, 0.15), 0 10px 10px -5px rgba(59, 130, 246, 0.1)'
            : 'inset 0 2px 0 0 transparent, inset 0 -2px 0 0 transparent';

          const firstCellShadow = isHovered
            ? 'inset 2px 2px 0 0 rgba(59, 130, 246, 0.8), inset 0 -2px 0 0 rgba(59, 130, 246, 0.8), 0 20px 25px -5px rgba(59, 130, 246, 0.15), 0 10px 10px -5px rgba(59, 130, 246, 0.1)'
            : 'inset 2px 2px 0 0 transparent, inset 0 -2px 0 0 transparent';

          const lastCellShadow = isHovered
            ? 'inset -2px 2px 0 0 rgba(59, 130, 246, 0.8), inset 0 -2px 0 0 rgba(59, 130, 246, 0.8), 0 20px 25px -5px rgba(59, 130, 246, 0.15), 0 10px 10px -5px rgba(59, 130, 246, 0.1)'
            : 'inset -2px 2px 0 0 transparent, inset 0 -2px 0 0 transparent';

          const baseCellStyle = isSafari
            ? {
                boxShadow: baseCellShadow,
                WebkitBoxShadow: baseCellShadow,
                transition: 'all 0.2s ease',
              }
            : {};

          const firstCellStyle = isSafari
            ? {
                boxShadow: firstCellShadow,
                WebkitBoxShadow: firstCellShadow,
                borderTopLeftRadius: '8px',
                borderBottomLeftRadius: '8px',
                transition: 'all 0.2s ease',
              }
            : undefined;

          const lastCellStyle = isSafari
            ? {
                boxShadow: lastCellShadow,
                WebkitBoxShadow: lastCellShadow,
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px',
                transition: 'all 0.2s ease',
              }
            : undefined;

          const cells = [
            <TableCell
              key='server-name'
              className={cn('cursor-pointer', !isSafari && 'group-hover:rounded-l-lg')}
              style={firstCellStyle}
              onMouseEnter={() => setNameHover(server.name)}
              onMouseLeave={() => setNameHover('')}
              onClick={() => onCopyName(server.name)}
            >
              <Tooltip
                content='Copied'
                isOpen={isNameCopy === server.name}
                closeDelay={100}
                classNames={{
                  content: 'bg-[#F9E79F]',
                }}
              >
                <div className='flex content-center justify-between'>
                  {server.name}
                  <DocumentDuplicateIcon
                    width={16}
                    color='gray'
                    className={cn(nameHover !== server.name && 'hidden')}
                  />
                </div>
              </Tooltip>
            </TableCell>,
            <TableCell
              key='server-host'
              className='cursor-pointer'
              style={baseCellStyle}
              onMouseEnter={() => setHostHover(server.host)}
              onMouseLeave={() => setHostHover('')}
              onClick={() => onCopyHost(server.host)}
            >
              <Tooltip
                content='Copied'
                isOpen={isHostCopy === server.host}
                closeDelay={100}
                classNames={{
                  content: 'bg-[#F9E79F]',
                }}
              >
                <div className='flex content-center justify-between'>
                  {server.host}
                  <DocumentDuplicateIcon
                    width={16}
                    color='gray'
                    className={cn(hostHover !== server.host && 'hidden')}
                  />
                </div>
              </Tooltip>
            </TableCell>,
            <TableCell key='cluster-tag' style={baseCellStyle}>
              {server.clusterTag}
            </TableCell>,
            <TableCell key='ssh-port' style={baseCellStyle}>
              {server.sshPort}
            </TableCell>,
            <TableCell key='php-port' style={baseCellStyle}>
              {server.phpPort}
            </TableCell>,
            <TableCell key='connection-type' style={baseCellStyle}>
              {connectionTypeMap[server.connectionType as ServerSchema['connectionType']]}
            </TableCell>,
            <TableCell key='ssh-username' style={baseCellStyle}>
              {server.sshUsername}
            </TableCell>,
            ...(showSslPasswords
              ? [
                  <TableCell key='ssl-password' style={baseCellStyle}>
                    {(() => {
                      const isSslEnabled = serverStats?.[server.uuid]?.stats?.services?.http_proxy?.active;

                      // If cluster filter is active and server is not in selected cluster, don't show
                      if (selectedCluster !== 'all' && server.clusterTag !== selectedCluster) {
                        return <span className='text-gray-400 text-sm'>-</span>;
                      }

                      // If server doesn't have SSL enabled, show "Not SSL server"
                      if (!isSslEnabled) {
                        return <span className='text-gray-400 text-sm'>Not SSL server</span>;
                      }

                      // Check if password is loading
                      if (sslPasswordLoading?.[server.uuid]) {
                        return <span className='text-gray-400 text-sm'>Loading...</span>;
                      }

                      // Check if there was an error
                      if (sslPasswordErrors?.[server.uuid]) {
                        return <span className='text-red-400 text-sm'>Error</span>;
                      }

                      // Check if password has been fetched
                      if (sslPasswords?.[server.uuid] !== undefined) {
                        if (sslPasswords[server.uuid] !== null) {
                          return <span className='font-mono text-sm'>{sslPasswords[server.uuid]}</span>;
                        } else {
                          return <span className='text-gray-400 text-sm'>Not SSL server</span>;
                        }
                      }

                      // Not fetched yet
                      return <span className='text-gray-400 text-sm'>Loading...</span>;
                    })()}
                  </TableCell>,
                ]
              : []),
            <TableCell key='actions' className={!isSafari ? 'group-hover:rounded-r-lg' : ''} style={lastCellStyle}>
              <div className='relative flex items-center gap-0'>
                <Tooltip
                  content='Reinitilaize Server'
                  classNames={{
                    content: 'bg-[#F9E79F]',
                  }}
                >
                  <Button
                    isIconOnly
                    variant='light'
                    className='min-w-6 w-6 h-6'
                    onPress={() => onReinitializeServer(server.uuid)}
                  >
                    <WrenchIcon className='h-4 w-4' />
                  </Button>
                </Tooltip>
                <Tooltip
                  content={
                    isServerFailed
                      ? 'Server may not be alive. Try connecting to test with ping mechanism.'
                      : terminalStatuses?.[server.uuid] === 'connected'
                        ? 'Terminal Connected'
                        : terminalStatuses?.[server.uuid] === 'connecting'
                          ? 'Terminal Connecting...'
                          : terminalStatuses?.[server.uuid] === 'error' ||
                              terminalStatuses?.[server.uuid] === 'disconnected'
                            ? 'Terminal Disconnected'
                            : 'Open Terminal'
                  }
                  classNames={{
                    content: 'bg-[#F9E79F]',
                  }}
                >
                  <div className='relative inline-block'>
                    <Button
                      isIconOnly
                      variant='light'
                      className='min-w-6 w-6 h-6'
                      onPress={() => onOpenServerTerminal(server)}
                    >
                      <CommandLineIcon className='h-4 w-4' />
                    </Button>
                    {/* Status indicator dot - positioned outside button to avoid clipping */}
                    {(isServerFailed ||
                      (terminalStatuses?.[server.uuid] && terminalStatuses[server.uuid] !== 'none')) && (
                      <span
                        className={cn(
                          'absolute top-0 right-0 w-2 h-2 rounded-full border border-white',
                          isServerFailed && 'bg-red-500',
                          !isServerFailed && terminalStatuses?.[server.uuid] === 'connected' && 'bg-green-500',
                          !isServerFailed &&
                            terminalStatuses?.[server.uuid] === 'connecting' &&
                            'bg-yellow-500 animate-pulse',
                          !isServerFailed &&
                            (terminalStatuses?.[server.uuid] === 'error' ||
                              terminalStatuses?.[server.uuid] === 'disconnected') &&
                            'bg-red-500'
                        )}
                      />
                    )}
                  </div>
                </Tooltip>
                <Tooltip
                  content={isServerFailed ? 'Server is in error state' : 'File Manager (SFTP)'}
                  classNames={{
                    content: 'bg-[#F9E79F]',
                  }}
                >
                  <Button
                    isIconOnly
                    variant='light'
                    className='min-w-6 w-6 h-6'
                    onPress={() => onOpenSftp(server)}
                    isDisabled={isServerFailed}
                  >
                    <FolderIcon className='h-4 w-4' />
                  </Button>
                </Tooltip>
                <Tooltip
                  content={
                    serverStats?.[server.uuid]?.stats?.services?.http_proxy?.active
                      ? 'Update SSL Password'
                      : 'SSL not enabled for this server'
                  }
                  classNames={{
                    content: 'bg-[#F9E79F]',
                  }}
                >
                  <Button
                    isIconOnly
                    variant='light'
                    className='min-w-6 w-6 h-6'
                    onPress={() => onUpdateSslPassword(server)}
                    isDisabled={!serverStats?.[server.uuid]?.stats?.services?.http_proxy?.active}
                  >
                    <KeyIcon className='h-4 w-4' />
                  </Button>
                </Tooltip>
                <Tooltip
                  content='Edit Server'
                  classNames={{
                    content: 'bg-[#F9E79F]',
                  }}
                >
                  <Button isIconOnly variant='light' className='min-w-6 w-6 h-6' onPress={() => onUpdateServer(server)}>
                    <PencilIcon className='h-4 w-4' />
                  </Button>
                </Tooltip>
                <Tooltip
                  content={isServerFailed ? 'Server is in error state' : 'Restart Server'}
                  classNames={{
                    content: 'bg-[#F9E79F]',
                  }}
                >
                  <Button
                    isIconOnly
                    variant='light'
                    className='min-w-6 w-6 h-6'
                    onPress={() => onRestartServer(server.uuid)}
                    isDisabled={isServerFailed}
                  >
                    <ArrowPathIcon className='h-4 w-4' />
                  </Button>
                </Tooltip>
                <IconTooltipPopoverUi
                  popoverPlacement='bottom'
                  tooltip='Delete Server'
                  icon={<TrashIcon className='h-4 w-4' />}
                >
                  {onClose => (
                    <ConfirmationUi
                      onButtonAPress={onClose}
                      buttonBColor='danger'
                      buttonAColor='primary'
                      buttonA='Cancel'
                      buttonB='Delete'
                      confirmationTittle='Delete server'
                      confirmationMessage={`Are you sure want to delete '${server.name}'?`}
                      onButtonBPress={() => onRemoveServer(server.uuid, onClose)}
                    />
                  )}
                </IconTooltipPopoverUi>
              </div>
            </TableCell>,
          ];

          return (
            <TableRow
              key={server.name}
              onMouseEnter={() => setHoveredRow(server.name)}
              onMouseLeave={() => setHoveredRow(null)}
              className={rowClassName}
            >
              {cells}
            </TableRow>
          );
        }) || []}
      </TableBody>
    </Table>
  );
};
